import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calculator, History, TrendingUp, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Calculator,
      title: 'Cálculo Preciso',
      description: 'Calcula la actualización de tu alquiler según IPC o IRAV con total precisión.'
    },
    {
      icon: TrendingUp,
      title: 'Datos en Tiempo Real',
      description: 'Índices actualizados directamente desde el Instituto Nacional de Estadística.'
    },
    {
      icon: History,
      title: 'Historial Completo',
      description: 'Guarda y consulta todos tus cálculos anteriores en cualquier momento.'
    },
    {
      icon: Shield,
      title: 'Conforme a la Ley',
      description: 'Cálculos basados en la Ley de Arrendamientos Urbanos y Ley de Vivienda.'
    }
  ];

  const handleStart = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero Section */}
      <section className="landing-hero relative px-6 py-20 md:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_mivau-clone/artifacts/ciik1x1e_logo.png" 
              alt="JBR Inmobiliaria" 
              className="h-32 md:h-40 w-auto mx-auto mb-8 fade-in"
              data-testid="hero-logo"
            />
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-8 fade-in">
              <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot"></span>
              Datos actualizados del INE
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 fade-in stagger-1" data-testid="hero-title">
              Actualiza tu alquiler
              <span className="block text-emerald-400">legalmente en segundos</span>
            </h1>
            
            <p className="text-lg text-zinc-400 mb-10 max-w-2xl mx-auto fade-in stagger-2">
              Calcula la subida máxima de tu renta según la legislación vigente.
              Distingue entre contratos anteriores y posteriores a la Ley de Vivienda.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 fade-in stagger-3">
              <Button 
                onClick={handleStart}
                className="w-full sm:w-auto h-12 px-8 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold btn-primary-glow transition-all"
                data-testid="cta-start-button"
              >
                Empezar ahora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/calculator')}
                className="w-full sm:w-auto h-12 px-8 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                data-testid="cta-quick-calc-button"
              >
                Cálculo rápido sin cuenta
              </Button>
            </div>
          </div>
          
          {/* Index Badges */}
          <div className="flex flex-wrap justify-center gap-4 mt-16 fade-in stagger-4">
            <div className="index-badge index-badge-ipc">
              <span className="text-zinc-400">IPC</span>
              <span className="text-lg font-bold">2.9%</span>
            </div>
            <div className="index-badge index-badge-irav">
              <span className="text-zinc-400">IRAV</span>
              <span className="text-lg font-bold">2.32%</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-20 border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Todo lo que necesitas para actualizar tu alquiler
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Herramienta completa basada en la normativa española vigente
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="bg-zinc-900/50 border-zinc-800 card-interactive"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* IPC vs IRAV Section */}
      <section className="px-6 py-20 border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-blue-500/5 border-blue-500/20">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <h3 className="text-xl font-bold text-white">Fórmula IPC</h3>
                </div>
                <p className="text-zinc-300 mb-6">
                  Para contratos de alquiler <strong>anteriores al 26 de mayo de 2023</strong> (antes de la Ley de Vivienda).
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-zinc-400">
                    <CheckCircle2 className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Se aplica el IPC general interanual</span>
                  </li>
                  <li className="flex items-start gap-2 text-zinc-400">
                    <CheckCircle2 className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Actualmente en el 2.9%</span>
                  </li>
                  <li className="flex items-start gap-2 text-zinc-400">
                    <CheckCircle2 className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Publicado mensualmente por el INE</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card className="bg-emerald-500/5 border-emerald-500/20">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <h3 className="text-xl font-bold text-white">Fórmula IRAV</h3>
                </div>
                <p className="text-zinc-300 mb-6">
                  Para contratos de alquiler <strong>a partir del 26 de mayo de 2023</strong> (Ley de Vivienda).
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-zinc-400">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Índice de Referencia de Arrendamientos de Vivienda</span>
                  </li>
                  <li className="flex items-start gap-2 text-zinc-400">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Actualmente en el 2.32%</span>
                  </li>
                  <li className="flex items-start gap-2 text-zinc-400">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Diseñado para limitar subidas excesivas</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_mivau-clone/artifacts/ciik1x1e_logo.png" alt="JBR Inmobiliaria" className="h-8 w-auto" />
              <span className="text-lg font-bold text-white">JBRentUpdate</span>
            </div>
            <p className="text-zinc-500 text-sm text-center md:text-right">
              Basado en datos del Instituto Nacional de Estadística (INE).
              <br className="md:hidden" />
              <span className="md:ml-1">Los resultados tienen carácter informativo.</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
